import type { Command } from '../state/commandRing';
import { shapeAxis, shapeThrottle, type AxisCurveConfig } from './curve';

export type FlightMode = 'acro' | 'angle';
export type LogicalAxis = 'roll' | 'pitch' | 'yaw' | 'throttle';
export type AuxChannel = 'arm' | 'modeToggle' | 'reset' | 'releaseManual';

export interface AxisBinding {
  index: number;
  curve: AxisCurveConfig;
}

/** Maps each logical flight channel to a physical axis and its shaping curve. */
export interface AxisMap {
  roll: AxisBinding;
  pitch: AxisBinding;
  yaw: AxisBinding;
  throttle: AxisBinding;
}

export interface AuxBinding {
  index: number;
  type: 'button' | 'axis';
  onThreshold: number;
}

export interface AuxMap {
  arm: AuxBinding;
  modeToggle: AuxBinding;
  reset: AuxBinding;
  releaseManual: AuxBinding;
}

/** Ticket 26 can persist and provide this profile shape per device hash. */
export interface InputProfile {
  axisMap: AxisMap;
  auxMap: AuxMap;
}

export interface AuxState {
  arm: boolean;
  modeToggle: boolean;
  reset: boolean;
  releaseManual: boolean;
}

export interface ResolvedChannels {
  roll: number;
  pitch: number;
  yaw: number;
  throttle: number;
  aux: AuxState;
}

const identityCurve = (): AxisCurveConfig => ({
  min: -1,
  max: 1,
  center: 0,
  deadzonePct: 0,
  reverse: false,
  expo: 0,
});

/** Generic AETR axes with the first four gamepad buttons used as aux inputs. */
export const DEFAULT_INPUT_PROFILE: InputProfile = {
  axisMap: {
    roll: { index: 0, curve: identityCurve() },
    pitch: { index: 1, curve: { ...identityCurve(), reverse: true } },
    throttle: { index: 2, curve: identityCurve() },
    yaw: { index: 3, curve: { ...identityCurve(), reverse: true } },
  },
  auxMap: {
    arm: { index: 0, type: 'button', onThreshold: 0.5 },
    modeToggle: { index: 1, type: 'button', onThreshold: 0.5 },
    reset: { index: 2, type: 'button', onThreshold: 0.5 },
    releaseManual: { index: 3, type: 'button', onThreshold: 0.5 },
  },
};

const rawAxis = (axes: readonly number[], binding: AxisBinding, fallback: number): number =>
  axes[binding.index] ?? fallback;

const auxIsOn = (
  binding: AuxBinding,
  rawAxes: readonly number[],
  rawButtons: readonly number[],
): boolean => {
  const value = binding.type === 'axis' ? rawAxes[binding.index] : rawButtons[binding.index];
  return (value ?? Number.NEGATIVE_INFINITY) >= binding.onThreshold;
};

export const channelsFromAxes = (
  rawAxes: readonly number[],
  rawButtons: readonly number[],
  profile: InputProfile,
): ResolvedChannels => {
  const { axisMap, auxMap } = profile;
  return {
    roll: shapeAxis(rawAxis(rawAxes, axisMap.roll, axisMap.roll.curve.center), axisMap.roll.curve),
    pitch: shapeAxis(
      rawAxis(rawAxes, axisMap.pitch, axisMap.pitch.curve.center),
      axisMap.pitch.curve,
    ),
    yaw: shapeAxis(rawAxis(rawAxes, axisMap.yaw, axisMap.yaw.curve.center), axisMap.yaw.curve),
    throttle: shapeThrottle(
      rawAxis(rawAxes, axisMap.throttle, axisMap.throttle.curve.min),
      axisMap.throttle.curve,
    ),
    aux: {
      arm: auxIsOn(auxMap.arm, rawAxes, rawButtons),
      modeToggle: auxIsOn(auxMap.modeToggle, rawAxes, rawButtons),
      reset: auxIsOn(auxMap.reset, rawAxes, rawButtons),
      releaseManual: auxIsOn(auxMap.releaseManual, rawAxes, rawButtons),
    },
  };
};

export const buildCommand = (channels: ResolvedChannels, mode: FlightMode): Command => ({
  roll: channels.roll,
  pitch: channels.pitch,
  yaw: channels.yaw,
  throttle: channels.throttle,
  mode,
  arm: channels.aux.arm,
  disarm: false,
  reset: channels.aux.reset,
  releaseManual: channels.aux.releaseManual,
  tuning: [],
});
